import { RubricComparison } from './reviews/RubricComparison'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { TaskPanel, TaskSectionLink } from './TaskWorkspace'
import { PersonName } from './PersonName'
import { api, ApiError } from '@/api/client'
import { fmtDate } from '@/lib/format'
import { validate } from '@/lib/schema'
import type { Role, RubricCriterion, RubricReview, TaskDetail } from '@/lib/types'
import { Section, Fold } from './Section'
import { RubricEditor, cleanRubric } from './RubricEditor'
import { RubricView } from './RubricView'
import { RubricWithChanges, reviewDiff } from './ReviewChanges'
import { FileDrop, MAX_FILES_PER_SLOT } from './FileDrop'
import { FileList } from './FileList'
import { ExamplesPanel } from './ExamplesPanel'

interface Props {
  data: TaskDetail
  role: Role
  onSaved: (data: TaskDetail) => void
  onDirty?: (dirty: boolean) => void
  otherDirty?: boolean
  compareImplementation?: boolean
  workspace?: boolean
}

const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

export function RubricRefinement({ data, role, onSaved, onDirty, otherDirty = false, workspace = false, compareImplementation = false }: Props) {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const { task, files, refinement: permissions, reviews = [] } = data
  const inBuild = task.stage === 3
  const [editing, setEditing] = useState(false)
  const [rubric, setRubric] = useState(task.rubric)
  const [base, setBase] = useState(task.rubric)
  const [layout, setLayout] = useState(task.rubric_layout)
  const [baseLayout, setBaseLayout] = useState(task.rubric_layout)
  const [revision, setRevision] = useState(task.revision ?? 0)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [conflict, setConflict] = useState(false)
  const dirty = editing && (!equal(rubric, base) || !equal(layout, baseLayout))
  useEffect(() => { onDirty?.(dirty || uploading || busy || (task.workflow_version === 2 && !!notes)) }, [dirty, uploading, busy, notes, task.workflow_version, onDirty])
  useEffect(() => () => onDirty?.(false), [onDirty])
  useEffect(() => {
    if (!editing) { setRubric(task.rubric); setBase(task.rubric); setLayout(task.rubric_layout); setBaseLayout(task.rubric_layout); setRevision(task.revision ?? 0) }
  }, [task, editing])

  async function refresh() {
    try { onSaved(await api.get<TaskDetail>(`/tasks/${task.task_id}`)); return true }
    catch (e) { setError((e as Error).message); return false }
  }
  useEffect(() => {
    const onFocus = () => { void refresh() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [task.task_id])
  const latestReturned = [...reviews].reverse().find((r) => r.status === 'returned')
  const active = reviews.find((r) => r.review_id === task.active_review_id)
  const pendingFiles = files.filter((f) => f.kind === 'model_results' && !f.review_id)
  const mine = !!permissions?.can_edit_rubric
  // Drafts use the draft editor, which also accepts unfinished criteria.
  const draft = task.lifecycle !== 'retired' && task.stage === 0 && role === 'sme'
  const handoff = !!(permissions?.can_request_review || permissions?.can_respond)
  const blocked = busy || uploading || dirty || otherDirty || !!permissions?.handoff_blockers.length
  const savedRubric = task.stage === 2 && reviewDiff(task)?.rubric.length
    ? <RubricWithChanges task={task} /> : <RubricView rubric={task.rubric} layout={task.rubric_layout} />

  async function failure(e: unknown) {
    setError(e instanceof ApiError ? [e.message, ...e.blockers].join(' ') : (e as Error).message)
    if (e instanceof ApiError && e.status === 409) { setConflict(false); if (await refresh()) setConflict(true) }
  }
  async function save() {
    const body = { rubric: cleanRubric(rubric), rubric_layout: layout, revision }
    const errors = validate('rubric_update', body)
    if (errors.length) { setError(errors.map((e) => `${e.path}: ${e.message}`).join(' ')); return }
    setBusy(true); setError('')
    try {
      const next = await api.put<TaskDetail>(`/${role === 'engineer' ? 'eng' : 'sme'}/tasks/${task.task_id}/rubric`, body)
      setEditing(false); setConflict(false); onSaved(next)
    } catch (e) { await failure(e) }
    finally { setBusy(false) }
  }
  async function send() {
    setBusy(true); setError('')
    try {
      const path = role === 'engineer'
        ? `/eng/tasks/${task.task_id}/rubric-reviews`
        : `/sme/tasks/${task.task_id}/rubric-reviews/${task.active_review_id}/respond`
      const next = await api.post<TaskDetail>(path, {
        revision: task.revision ?? 0, notes,
        ...(role === 'engineer' ? { file_ids: pendingFiles.filter((f) => f.status === 'uploaded').map((f) => f.file_id) } : {}),
      })
      setNotes(''); setEditing(false); onSaved(next)
    } catch (e) { await failure(e) }
    finally { setBusy(false) }
  }

  const rubricContent = <Section id="task-rubric" title="Rubric" lead="The criteria used to judge a result."
    aside={(mine || draft) && !editing ? <button className="sm" disabled={otherDirty || busy || uploading} onClick={() => {
      if (draft) { navigate(`/sme/tasks/${task.task_id}/edit?section=rubric`); return }
      setEditing(true); setError(''); setConflict(false)
    }}>Edit rubric</button> : undefined}>
      {editing ? <div>
        <RubricEditor value={rubric} onChange={setRubric} layout={layout} onLayoutChange={setLayout} reservedIds={[...task.retired_criterion_ids ?? [], ...task.rubric.map(c => c.criterion_id)]}
          templates={role === 'sme' && task.kind !== 'scientific_workflow'} />
        <p className="muted small">{inBuild ? 'Changes to criteria require the affected verifier checks to be repeated. Reorganising sections keeps existing checks and evidence.' : 'Existing uploads are kept. Check that your examples still match the updated criteria.'}</p>
        <div className="row">
          <button className="primary sm" disabled={busy || !dirty || !mine} onClick={save}>Save rubric</button>
          <button className="sm" disabled={busy} onClick={() => { setEditing(false); setConflict(false); setError('') }}>Discard edits</button>
          {dirty && <span className="small muted">Unsaved changes</span>}
        </div>
        {conflict && <div className="note warn" style={{ marginTop: '.75rem' }}>
          <p>Your edits are kept above. Compare them with the latest saved rubric before trying again.</p>
          <Fold title="Latest saved rubric"><RubricView rubric={task.rubric} layout={task.rubric_layout} /></Fold>
          {mine && <button className="sm" onClick={() => { setRevision(task.revision ?? 0); setConflict(false) }}>I’ve reviewed the latest rubric</button>}
        </div>}
      </div> : compareImplementation ? <RubricComparison task={task} role={role} authorRubric={savedRubric} /> : savedRubric}
      {files.some((f) => f.criterion_id && !task.rubric.some((c) => c.criterion_id === f.criterion_id)) && <Fold title="Examples for removed criteria">
        <FileList files={files.filter((f) => f.criterion_id && !task.rubric.some((c) => c.criterion_id === f.criterion_id))} showKind />
      </Fold>}
      {inBuild && role === 'sme' && task.verify && !editing && <Fold title="Passing and failing examples">
        <p className="muted small">Keep examples that still fit. Upload replacements when the expected result changes; new criteria need both examples.</p>
        <ExamplesPanel taskId={task.task_id} rubric={task.rubric} files={files} editable={!!permissions?.can_edit_examples} onChanged={refresh} onBusy={setUploading} />
      </Fold>}
    {error && <div role="alert" className="note err">{error}</div>}
  </Section>
  const reviewContent = <Section title={active ? 'Expert review' : latestReturned ? 'Expert feedback' : 'Refine with the expert'}>
      {!active && latestReturned && role === 'engineer' && <div className="review-message">
        <div className="small muted">Expert review returned by <PersonName people={data.people} id={latestReturned.returned_by_id}
          email={latestReturned.returned_by} fallback="the expert" /> · {fmtDate(latestReturned.returned_at)}</div>
        <p className="prewrap">{latestReturned.response}</p>
        <Fold title="Changes from the expert"><Changes review={latestReturned} /></Fold>
      </div>}
      {active && <div className="review-message">
        <div className="small muted">From <PersonName people={data.people} id={active.requested_by_id} email={active.requested_by} /> · {fmtDate(active.requested_at)}</div>
        <p className="prewrap">{active.notes}</p>
        <FileList files={files.filter((f) => active.file_ids.includes(f.file_id))} />
      </div>}
    {workspace && <div className="row"><TaskSectionLink section="rubric">{mine ? 'Edit rubric & examples' : 'View rubric'}</TaskSectionLink></div>}
    <details hidden={!handoff} className="review-compose" open={!!active || params.get('review') === '1'} onToggle={e => {
      if (!workspace || active) return
      const open = e.currentTarget.open
      if (open === (params.get('review') === '1')) return
      const next = new URLSearchParams(params)
      if (open) next.set('review', '1'); else next.delete('review')
      setParams(next, { replace: true })
    }}>
      <summary>{role === 'engineer' ? 'Request expert review' : 'Return to developer'}</summary>
      {handoff && <div className="sub">
        <h3>{role === 'engineer' ? 'Ask the expert to review' : 'Return your review'}</h3>
        {permissions?.can_upload_results && <>
          <FileList files={pendingFiles} canDelete={!busy && !uploading && !editing} onChanged={refresh} />
          {!editing && pendingFiles.length < MAX_FILES_PER_SLOT && <FileDrop taskId={task.task_id} meta={{ kind: 'model_results' }} label="Attach model outputs and scores" hint="Optional · include model names, outputs and their scores" remaining={MAX_FILES_PER_SLOT - pendingFiles.length} onUploaded={() => {}} onSettled={refresh} onBusy={setUploading} />}
          {!editing && pendingFiles.length >= MAX_FILES_PER_SLOT && <p className="muted small">This review already holds {MAX_FILES_PER_SLOT} files (the maximum). Remove one to attach another.</p>}
        </>}
        <label className="field" style={{ marginTop: '1rem' }}><span>{role === 'engineer' ? 'What should the expert check?' : 'What did you change or confirm?'}</span><textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={role === 'engineer' ? 'Describe the scoring mismatches or borderline results you want reviewed.' : 'Explain your changes, or confirm that the rubric already matches your judgment.'} /></label>
        {(dirty || otherDirty) && <p className="muted small">Save or discard your changes before handing off.</p>}
        {!!permissions?.handoff_blockers.length && <ul className="checklist">{permissions.handoff_blockers.map((b) => <li key={b}>{b}</li>)}</ul>}
        <button className="primary" disabled={blocked || !notes.trim()} onClick={send}>{busy ? 'Saving…' : role === 'engineer' ? 'Request expert review' : 'Return to developer'}</button>
        {task.workflow_version === 2 && notes && <button className="link small" disabled={busy} onClick={() => setNotes('')}>Discard draft</button>}
      </div>}
    </details>
    {!handoff && <p className="muted small">{active ? 'Editing returns to the developer when the expert replies.' : 'A new review request will appear here when the developer needs your input.'}</p>}
    {error && <div role="alert" className="note err">{error}</div>}
  </Section>
  // The developer's build stage has no Work tab -- the agent owns it -- so the review
  // hand-off rides along on Monitor there. The expert's portal is unchanged.
  if (workspace) return <>
    {inBuild && <TaskPanel section={role === 'engineer' ? 'monitor' : 'work'}>{reviewContent}</TaskPanel>}
    <TaskPanel section="rubric">{rubricContent}</TaskPanel>
  </>
  return <>{rubricContent}{inBuild && <>{reviewContent}<ReviewHistory data={data} /></>}</>
}

function Changes({ review }: { review: RubricReview }) {
  const before = review.rubric_before
  const after = review.rubric_after ?? before
  const changes = [...new Set([...before, ...after].map((c) => c.criterion_id))].flatMap((id) => {
    const old = before.find((c) => c.criterion_id === id), current = after.find((c) => c.criterion_id === id)
    if (equal(old, current)) return []
    return [{ id, old, current }]
  })
  const organisationChanged = !equal(review.rubric_layout_before ?? [], review.rubric_layout_after ?? review.rubric_layout_before ?? [])
  if (!changes.length && !organisationChanged) return <p className="muted small">Rubric confirmed without changes.</p>
  const properties = (c: RubricCriterion) => Object.entries(c).filter(([k]) => k !== 'criterion_id' && k !== 'pass_gate').map(([k, v]) => <div key={k}><strong>{k.replaceAll('_', ' ')}:</strong> {String(v)}</div>)
  return <div className="stack">{organisationChanged && <Fold title="Rubric organisation changed">
    <Fold title="Before"><RubricView rubric={before} layout={review.rubric_layout_before} /></Fold>
    <Fold title="After"><RubricView rubric={after} layout={review.rubric_layout_after ?? undefined} /></Fold>
  </Fold>}{changes.map((c) => <Fold key={c.id} title={`${c.id} · ${!c.old ? 'added' : !c.current ? 'removed' : 'changed'}`}>
    {c.old && <div className="review-message"><div className="small muted">Before</div>{properties(c.old)}</div>}
    {c.current && <div><div className="small muted">After</div>{properties(c.current)}</div>}
  </Fold>)}</div>
}

export function ReviewHistory({ data }: { data: TaskDetail }) {
  const completed = (data.reviews ?? []).filter((r) => r.status === 'returned' || r.status === 'superseded')
  if (!completed.length) return null
  return <div className="section"><Fold title="Earlier rubric reviews" count={completed.length}>
    {[...completed].reverse().map((r) => <Fold key={r.review_id} title={`Review · ${fmtDate(r.requested_at)}`}>
      <div className="small muted"><PersonName people={data.people} id={r.requested_by_id} email={r.requested_by} /></div><p className="prewrap">{r.notes}</p>
      <FileList files={data.files.filter((f) => r.file_ids.includes(f.file_id))} />
      {r.status === 'superseded' ? <p className="small muted">Closed when task retired.</p> : <>
        <div className="review-message"><div className="small muted"><PersonName people={data.people} id={r.returned_by_id} email={r.returned_by} /> · {fmtDate(r.returned_at)}</div><p className="prewrap">{r.response}</p></div>
        <Changes review={r} />
      </>}
    </Fold>)}
  </Fold></div>
}
